import _ from 'underscore';
import React, {
  useCallback, useContext, useEffect, useMemo,
} from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  mergeClasses,
} from '@fluentui/react-components';
import { useFormService, FORM_STATES, FORM_EVENTS } from '@bingads-webui-react/form-core';

interface IDialogConfig {
  disableConfirmPopup?: boolean,
  className?: string,
  title?: string,
  content?: string,
  confirmText?: string,
  abortText?: string,
  onConfirmFn?: () => void,
  onCancelFn?: () => void,
}

interface ConfirmDialogProps {
  config?: IDialogConfig | ((stateMatch?) => any);
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = (props) => {
  const i18n = useContext(I18nContext) as any;
  const { state, send } = useFormService();
  const shouldShowConfirmDialog = state.matches(FORM_STATES.CONFIRMING);
  const useConfig = useMemo(() => (_.isFunction(props.config) ? props.config : () => props.config), [props.config]);

  const {
    disableConfirmPopup = false,
    className = null,
    title = null,
    content = null,
    confirmText = null,
    abortText = null,
    onConfirmFn = null,
    onCancelFn = null,
  } = useConfig(shouldShowConfirmDialog) || {};

  const onConfirm = useCallback(() => {
    if (onConfirmFn) {
      onConfirmFn();
    }
    send(FORM_EVENTS.CONFIRM_SAVE);
  }, [onConfirmFn, send]);

  const onCancel = useCallback(() => {
    if (onCancelFn) {
      onCancelFn();
    }
    send(FORM_EVENTS.ABORT_SAVE);
  }, [onCancelFn, send]);

  useEffect(() => {
    if (disableConfirmPopup && shouldShowConfirmDialog) {
      onConfirm();
    }
  }, [disableConfirmPopup, onConfirm, shouldShowConfirmDialog]);

  return (
    <Dialog open={shouldShowConfirmDialog && !disableConfirmPopup}>
      <DialogSurface className={mergeClasses('form-confirm-dialog', className)}>
        <DialogBody>
          <DialogTitle>{title || i18n.getString(_TL_('Are you sure you want to save these changes?'))}</DialogTitle>
          <DialogContent>
            {content || i18n.getString(_TL_('Please review your modifications before proceeding.'))}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              className="confirm-dialog-confirm"
              onClick={onConfirm}
            >
              {confirmText || i18n.getString(_TL_('Yes, save changes'))}
            </Button>
            <Button
              appearance="secondary"
              className="confirm-dialog-cancel"
              onClick={onCancel}
            >
              {abortText || i18n.getString(_TL_('Cancel'))}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
